import { useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import PropTypes from "prop-types";

import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { adminRequest } from "services/adminApi";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const numberFormat = new Intl.NumberFormat("ar");
const serviceLabels = {
  flight_status: "حالة الرحلة",
  more_services: "المزيد من الخدمات",
  transportation_whatsapp: "المواصلات عبر واتساب",
  marine_booking_whatsapp: "الحجز البحري عبر واتساب",
  entry_assistance_whatsapp: "المساعدة في الدخول عبر واتساب",
  currency_converter: "تحويل العملات",
  travel_translation: "مترجم السفر",
  budget: "ميزانية الرحلة",
  preparations: "تجهيزات السفر",
};

const primaryCards = [
  ["newInstalls", "تثبيتات جديدة", "أول فتح للتطبيق خلال الفترة", false],
  ["activeInstallations", "مستخدمون نشطون", "أجهزة استخدمت التطبيق فعلياً", false],
  ["activationRate", "معدل التفعيل", "إجراء مفيد خلال أول 7 أيام", true],
  ["signupConversion", "إكمال التسجيل", "بدأ التسجيل وأنشأ حساباً", true],
  ["commercialClicks", "نقرات تجارية", "حجز أو تواصل مع شريك", false],
  ["d7Retention", "الاحتفاظ D7", "عاد في اليوم السابع", true],
];

const secondaryCards = [
  ["dau", "نشطون اليوم"],
  ["wau", "نشطون خلال 7 أيام"],
  ["mau", "نشطون خلال 30 يوماً"],
  ["stickiness", "DAU / MAU", true],
  ["tripsCreated", "رحلات أُنشئت"],
  ["flightSearches", "عمليات بحث طيران"],
  ["notificationOpens", "فتح إشعارات المقالات"],
];

function displayValue(metric, percent = false) {
  if (metric?.value == null) return "—";
  return percent ? `${metric.value}%` : numberFormat.format(metric.value);
}

function GrowthCard({ title, description, metric, percent }) {
  const change = metric?.changePercent;
  const changeColor = change == null ? "default" : change >= 0 ? "success" : "error";
  const changeLabel =
    change == null ? "لا توجد مقارنة" : `${change > 0 ? "+" : ""}${change}% عن الفترة السابقة`;
  return (
    <Card sx={{ height: "100%", p: 2.5, border: "1px solid #E7EFEF", boxShadow: "none" }}>
      <Typography variant="body2" color="text.secondary" fontWeight={700}>
        {title}
      </Typography>
      <Typography variant="h4" fontWeight={800} color="#12313F" mt={0.75}>
        {displayValue(metric, percent)}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
        {description}
      </Typography>
      <Chip
        label={changeLabel}
        color={changeColor}
        size="small"
        variant="outlined"
        sx={{ mt: 1.5 }}
      />
    </Card>
  );
}

GrowthCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  metric: PropTypes.shape({
    value: PropTypes.number,
    previous: PropTypes.number,
    changePercent: PropTypes.number,
  }),
  percent: PropTypes.bool,
};

GrowthCard.defaultProps = {
  metric: null,
  percent: false,
};

function RankingTable({ title, rows, emptyLabel }) {
  return (
    <Card sx={{ p: 2.5, height: "100%", boxShadow: "none", border: "1px solid #E7EFEF" }}>
      <Typography variant="h6" fontWeight={800} mb={2}>
        {title}
      </Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {emptyLabel}
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>العنصر</TableCell>
              <TableCell align="center">النقرات</TableCell>
              <TableCell align="center">المستخدمون</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.key}>
                <TableCell>{row.label || serviceLabels[row.key] || row.key}</TableCell>
                <TableCell align="center">{numberFormat.format(row.clicks)}</TableCell>
                <TableCell align="center">{numberFormat.format(row.users)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}

RankingTable.propTypes = {
  title: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string,
      clicks: PropTypes.number.isRequired,
      users: PropTypes.number.isRequired,
    })
  ).isRequired,
  emptyLabel: PropTypes.string.isRequired,
};

export default function AnalyticsPage() {
  const [days, setDays] = useState(30);
  const [platform, setPlatform] = useState("all");
  const [versionDraft, setVersionDraft] = useState("");
  const [appVersion, setAppVersion] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    const query = new URLSearchParams({ days: String(days), platform });
    if (appVersion) query.set("appVersion", appVersion);
    adminRequest(`/admin/analytics?${query}`)
      .then((payload) => {
        if (active) setData(payload);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [days, platform, appVersion]);

  const chartData = useMemo(() => {
    const series = data?.timeSeries || [];
    return {
      labels: series.map((item) => item.date.slice(5)),
      datasets: [
        {
          label: "المستخدمون النشطون",
          data: series.map((item) => item.activeInstallations),
          borderColor: "#179B82",
          backgroundColor: "#179B82",
          tension: 0.3,
        },
        {
          label: "التثبيتات الجديدة",
          data: series.map((item) => item.newInstalls),
          borderColor: "#E7A83E",
          backgroundColor: "#E7A83E",
          tension: 0.3,
        },
        {
          label: "النقرات التجارية",
          data: series.map((item) => item.commercialClicks),
          borderColor: "#3478C8",
          backgroundColor: "#3478C8",
          tension: 0.3,
        },
      ],
    };
  }, [data]);

  const maxFunnel = Math.max(...(data?.funnel || []).map((step) => step.value), 1);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Box mb={2.5}>
          <Typography variant="h4" fontWeight={900} color="#12313F">
            النمو والتحليلات
          </Typography>
          <Typography color="text.secondary" mt={0.5}>
            مؤشرات تساعدنا على معرفة ما يجذب المسافرين وما يحوّلهم إلى مستخدمين دائمين.
          </Typography>
        </Box>
        <Card sx={{ p: 2, mb: 3, boxShadow: "none", border: "1px solid #E7EFEF" }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.25}
            alignItems={{ xs: "stretch", md: "center" }}
            flexWrap="wrap"
            useFlexGap
          >
            <Typography variant="body2" fontWeight={800} sx={{ ml: { md: 1 } }}>
              عرض البيانات:
            </Typography>
            <Stack direction="row" spacing={0.5}>
              {[7, 30, 90].map((value) => (
                <Button
                  key={value}
                  size="small"
                  variant={days === value ? "contained" : "outlined"}
                  onClick={() => setDays(value)}
                  sx={days === value ? { bgcolor: "#179B82" } : { color: "#179B82" }}
                >
                  {value} يوم
                </Button>
              ))}
            </Stack>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>المنصة</InputLabel>
              <Select
                value={platform}
                label="المنصة"
                onChange={(event) => setPlatform(event.target.value)}
              >
                <MenuItem value="all">كل المنصات</MenuItem>
                <MenuItem value="ios">iOS</MenuItem>
                <MenuItem value="android">Android</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="إصدار التطبيق"
              placeholder="مثال 1.2.3"
              value={versionDraft}
              onChange={(event) => setVersionDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") setAppVersion(versionDraft.trim());
              }}
              sx={{ width: { xs: "100%", md: 180 } }}
            />
            <Button variant="outlined" onClick={() => setAppVersion(versionDraft.trim())}>
              تطبيق
            </Button>
          </Stack>
        </Card>

        {error && <Alert severity="error">{error}</Alert>}
        {loading && !data ? (
          <MDBox minHeight="55vh" display="flex" alignItems="center" justifyContent="center">
            <CircularProgress color="success" />
          </MDBox>
        ) : null}

        {data ? (
          <Box sx={{ opacity: loading ? 0.55 : 1, transition: "opacity .2s" }}>
            <Grid container spacing={2}>
              {primaryCards.map(([key, title, description, percent]) => (
                <Grid item xs={12} sm={6} lg={4} key={key}>
                  <GrowthCard
                    title={title}
                    description={description}
                    metric={data.kpis[key]}
                    percent={percent}
                  />
                </Grid>
              ))}
            </Grid>

            <Card sx={{ mt: 3, p: 2.5, boxShadow: "none", border: "1px solid #E7EFEF" }}>
              <Typography variant="h6" fontWeight={800} mb={2}>
                نشاط التطبيق
              </Typography>
              <Grid container spacing={2}>
                {secondaryCards.map(([key, label, percent]) => (
                  <Grid item xs={6} md={3} lg key={key}>
                    <Box sx={{ bgcolor: "#F6FAFA", borderRadius: 2, p: 2, height: "100%" }}>
                      <Typography variant="caption" color="text.secondary">
                        {label}
                      </Typography>
                      <Typography variant="h5" fontWeight={800} color="#12313F">
                        {displayValue(data.kpis[key], percent)}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Card>

            <Grid container spacing={3} mt={0}>
              <Grid item xs={12} lg={8}>
                <Card sx={{ p: 2.5, boxShadow: "none", border: "1px solid #E7EFEF" }}>
                  <Typography variant="h6" fontWeight={800} mb={2}>
                    التغير اليومي
                  </Typography>
                  <Box sx={{ height: 320 }}>
                    <Line
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: { mode: "index", intersect: false },
                        plugins: { legend: { position: "bottom", rtl: true } },
                        scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
                      }}
                    />
                  </Box>
                </Card>
              </Grid>
              <Grid item xs={12} lg={4}>
                <Card
                  sx={{ p: 2.5, height: "100%", boxShadow: "none", border: "1px solid #E7EFEF" }}
                >
                  <Typography variant="h6" fontWeight={800} mb={2}>
                    مسار التحويل
                  </Typography>
                  <Stack spacing={2}>
                    {data.funnel.map((step) => (
                      <Box key={step.key}>
                        <Stack direction="row" justifyContent="space-between" mb={0.5}>
                          <Typography variant="body2" fontWeight={700}>
                            {step.label}
                          </Typography>
                          <Typography variant="body2">{numberFormat.format(step.value)}</Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={(step.value * 100) / maxFunnel}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            "& .MuiLinearProgress-bar": { bgcolor: "#179B82" },
                          }}
                        />
                      </Box>
                    ))}
                  </Stack>
                  <Alert severity="info" sx={{ mt: 2.5 }}>
                    المراحل تعرض مستخدمين فريدين داخل الفترة، وليست ادعاءً بأن كل شخص مر بجميع
                    المراحل.
                  </Alert>
                </Card>
              </Grid>
            </Grid>

            <Grid container spacing={3} mt={0}>
              <Grid item xs={12} lg={6}>
                <RankingTable
                  title="أكثر الخدمات استخداماً"
                  rows={data.topServices}
                  emptyLabel="ستظهر الخدمات بعد وصول أحداث الاستخدام الجديدة."
                />
              </Grid>
              <Grid item xs={12} lg={6}>
                <RankingTable
                  title="أكثر المقالات فتحاً"
                  rows={data.topArticles}
                  emptyLabel="لا توجد عمليات فتح مقالات خلال الفترة."
                />
              </Grid>
            </Grid>

            <Grid container spacing={3} mt={0}>
              <Grid item xs={12} md={7}>
                <Card
                  sx={{ p: 2.5, height: "100%", boxShadow: "none", border: "1px solid #E7EFEF" }}
                >
                  <Typography variant="h6" fontWeight={800} mb={2}>
                    أداء فعاليات الشهر
                  </Typography>
                  <Grid container spacing={2}>
                    {[
                      ["مرات الظهور", data.monthlyEvents.impressions],
                      ["مرات الفتح", data.monthlyEvents.opens],
                      ["نقرات الحجز", data.monthlyEvents.bookingClicks],
                      ["معدل الفتح", data.monthlyEvents.openRate, true],
                      ["التحويل للحجز", data.monthlyEvents.bookingRate, true],
                    ].map(([label, value, percent]) => (
                      <Grid item xs={6} sm key={label}>
                        <Typography variant="caption" color="text.secondary">
                          {label}
                        </Typography>
                        <Typography variant="h6" fontWeight={800}>
                          {value == null ? "—" : percent ? `${value}%` : numberFormat.format(value)}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Card>
              </Grid>
              <Grid item xs={12} md={5}>
                <Card
                  sx={{ p: 2.5, height: "100%", boxShadow: "none", border: "1px solid #E7EFEF" }}
                >
                  <Typography variant="h6" fontWeight={800} mb={1.5}>
                    سلامة البيانات
                  </Typography>
                  <Stack spacing={1}>
                    <Typography variant="body2">
                      الأحداث خلال الفترة:{" "}
                      <b>{numberFormat.format(data.dataQuality.totalEvents)}</b>
                    </Typography>
                    <Typography variant="body2">
                      أجهزة لها بيانات:{" "}
                      <b>{numberFormat.format(data.dataQuality.trackedInstallations)}</b>
                    </Typography>
                    <Typography variant="body2">
                      حجم عينة D7: <b>{numberFormat.format(data.dataQuality.d7CohortSize)}</b>
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                    البيانات الخام تُحفظ 90 يوماً ولا تعرض هذه الصفحة أي معرّف جهاز أو بيانات شخصية.
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Box>
        ) : null}
      </MDBox>
    </DashboardLayout>
  );
}
